/**
 * Iter 425.f (Dirk, 2026-03-01) — Bulk service recording modal.
 *
 * User brief: "Record service" button that lets ops multi-select on-site
 * serviceable equipment, set qty + date, optionally link a job card.
 * Save advances last + next service dates. If the linked JC's picking
 * materials don't include the equipment, a warning surfaces in-modal
 * BEFORE we call save so ops can back out or acknowledge and proceed.
 *
 * Two-step flow:
 *   1. Select entries + service date + optional JC.
 *   2. Confirm dates (edit next_service_date inline; if changed, ask
 *      whether to update the interval permanently or just this cycle).
 */
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckSquare, Loader2, Square, X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";
import DictTextarea from "../DictTextarea";

const iso = (d) => new Date(d).toISOString().slice(0, 10);

function addDays(dateStr, days) {
  if (!dateStr || !days) return null;
  const d = new Date(dateStr);
  d.setDate(d.getDate() + Number(days));
  return iso(d);
}

export default function RecordServiceModal({
  equipment,
  siteId,
  siteName,
  customerId,
  presetEquipmentIds = [],  // Iter 425.f — pre-check ids for quick-service.
  onClose,
  onSaved,
}) {
  // Iter 427.p48 (WS3, Dirk 2026-02-20) — "Show non-serviceable
  // items" toggle. DEFAULT OFF: normal ops flow hides replaced /
  // repaired items so the tick-list stays clean. Turn ON when
  // (rarely) needing to log a service against an item that was
  // marked non-serviceable — e.g. re-commissioning after a repair.
  const [showNonServiceable, setShowNonServiceable] = useState(false);

  // Serviceable equipment on this site. Non-serviceable rows are
  // hidden unless `showNonServiceable` is on.
  const candidates = useMemo(() => {
    return (equipment || [])
      .filter((e) => e.site_id === siteId)
      .filter((e) => showNonServiceable ? true : e.serviceable !== false)
      .filter((e) => !e.is_group)  // parent groups are containers, not serviced directly
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [equipment, siteId, showNonServiceable]);

  const [step, setStep] = useState("pick");  // "pick" | "confirm"
  const [selected, setSelected] = useState(() => new Set(presetEquipmentIds));
  const [qtyOverride, setQtyOverride] = useState({});
  const [serviceDate, setServiceDate] = useState(iso(new Date()));
  const [jcId, setJcId] = useState("");
  const [jcOptions, setJcOptions] = useState([]);
  const [notes, setNotes] = useState("");
  const [confirmRows, setConfirmRows] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [intervalPrompts, setIntervalPrompts] = useState({});
  const [saving, setSaving] = useState(false);

  // Load site's jobcards for the optional link picker.
  useEffect(() => {
    if (!customerId) return;
    (async () => {
      try {
        const { data } = await api.get(`/jobcards`, {
          params: { customer_id: customerId, site_id: siteId, limit: 50 },
        });
        const list = Array.isArray(data) ? data : (data?.items || []);
        setJcOptions(list.slice(0, 50));
      } catch {
        setJcOptions([]);
      }
    })();
  }, [customerId, siteId]);

  const toggle = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const goConfirm = () => {
    if (selected.size === 0) {
      toast.error("Select at least one item.");
      return;
    }
    // Build the confirmation rows with locally-computed next dates so
    // the user sees exactly what will be saved.
    const rows = candidates
      .filter((e) => selected.has(e.id))
      .map((e) => {
        const nextAuto = addDays(serviceDate, e.interval_days);
        return {
          equipment_id: e.id,
          name: e.name,
          interval_days: e.interval_days || null,
          quantity: qtyOverride[e.id] ?? e.quantity ?? 1,
          last_service_date: serviceDate,
          next_service_date: nextAuto,
          next_service_date_original: nextAuto,
          update_interval_permanent: false,
        };
      });
    setConfirmRows(rows);
    setStep("confirm");
  };

  const patchRow = (id, patch) => {
    setConfirmRows((rows) => rows.map((r) => (r.equipment_id === id ? { ...r, ...patch } : r)));
    // If next_service_date changed away from the original, ask about
    // permanent-interval treatment (once per row until user answers).
    if (patch.next_service_date !== undefined) {
      setIntervalPrompts((prev) => {
        const row = confirmRows.find((r) => r.equipment_id === id);
        if (!row) return prev;
        if (patch.next_service_date && patch.next_service_date !== row.next_service_date_original) {
          return { ...prev, [id]: true };
        }
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        site_id: siteId,
        service_date: serviceDate,
        jobcard_id: jcId || null,
        notes: notes || null,
        entries: confirmRows.map((r) => ({
          equipment_id: r.equipment_id,
          quantity: Number(r.quantity) || null,
          next_service_date: r.next_service_date || null,
          update_interval_permanent: !!r.update_interval_permanent,
        })),
      };
      const { data } = await api.post("/equipment/service-bulk", payload);
      const warns = data?.warnings || [];
      if (warns.some((w) => w.reason === "not_on_picking")) {
        setWarnings(warns);
        return;  // Keep modal open so ops sees the alert.
      }
      const n = confirmRows.length;
      toast.success(`${n} item${n === 1 ? "" : "s"} recorded as serviced.`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  // Iter 425.f — Called after ops acknowledges the "not on picking"
  // warning. Re-invokes save with warnings dismissed so we don't loop.
  const acknowledgeAndSave = async () => {
    setWarnings([]);
    // Second call will hit the same warnings but we short-circuit here
    // by treating the SECOND save as a "user has read the alert" and
    // don't re-block. Cleanest is to remember and skip the block.
    setSaving(true);
    try {
      const payload = {
        site_id: siteId,
        service_date: serviceDate,
        jobcard_id: jcId || null,
        notes: notes || null,
        entries: confirmRows.map((r) => ({
          equipment_id: r.equipment_id,
          quantity: Number(r.quantity) || null,
          next_service_date: r.next_service_date || null,
          update_interval_permanent: !!r.update_interval_permanent,
        })),
      };
      await api.post("/equipment/service-bulk", payload);
      const n = confirmRows.length;
      toast.success(`${n} item${n === 1 ? "" : "s"} recorded (warnings acknowledged).`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="record-service-modal">
      <div className="bg-[#0a0b0e] border border-white/10 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div>
            <div className="text-sm font-bold">Record service</div>
            <div className="text-[10px] text-[#647087]">
              {siteName || "Site"} · {step === "pick" ? "Step 1 · Select equipment" : "Step 2 · Confirm dates"}
            </div>
          </div>
          <button type="button" onClick={onClose}
                  className="p-1 hover:bg-white/5 rounded"
                  data-testid="record-service-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {step === "pick" && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1 col-span-1">
                  Service date
                  <input type="date"
                         value={serviceDate}
                         onChange={(e) => setServiceDate(e.target.value)}
                         className="hv-input text-xs"
                         data-testid="record-service-date" />
                </label>
                <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1 col-span-1 md:col-span-2">
                  Job card (optional)
                  <select value={jcId}
                          onChange={(e) => setJcId(e.target.value)}
                          className="hv-input text-xs"
                          data-testid="record-service-jc">
                    <option value="">— none —</option>
                    {jcOptions.map((jc) => (
                      <option key={jc.id} value={jc.id}>
                        {jc.short_id ? `${jc.short_id} · ` : ""}{jc.title || jc.id.slice(0, 8)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold block">
                Notes (optional)
                <DictTextarea value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          rows={2}
                          className="hv-input text-xs mt-1 w-full"
                          data-testid="record-service-notes" />
              </label>
              <div className="border border-white/10 rounded overflow-hidden">
                <div className="px-3 py-2 bg-white/[0.03] text-[10px] uppercase tracking-wider text-[#647087] font-bold flex items-center justify-between gap-2 flex-wrap">
                  <span>Equipment on site ({candidates.length})</span>
                  <div className="flex items-center gap-3">
                    {/* Iter 427.p48 (WS3) — Show non-serviceable items
                        checkbox. Default OFF. Pull rarely-serviced
                        replaced / repaired items back into the list
                        without leaving the modal. */}
                    <label
                      className="flex items-center gap-1.5 cursor-pointer normal-case tracking-normal text-[10px] text-[#a0aabf] font-normal"
                      data-testid="record-service-show-non-serviceable-label"
                    >
                      <input
                        type="checkbox"
                        checked={showNonServiceable}
                        onChange={(e) => setShowNonServiceable(e.target.checked)}
                        className="cursor-pointer"
                        data-testid="record-service-show-non-serviceable"
                      />
                      <span>Show non-serviceable items</span>
                    </label>
                    <span>{selected.size} selected</span>
                  </div>
                </div>
                <div className="divide-y divide-white/5 max-h-[40vh] overflow-y-auto">
                  {candidates.length === 0 && (
                    <div className="p-3 text-[11px] text-[#647087]">
                      No serviceable equipment on this site.
                    </div>
                  )}
                  {candidates.map((e) => {
                    const isSel = selected.has(e.id);
                    const isNonServiceable = e.serviceable === false;
                    return (
                      <div key={e.id}
                           className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-xs ${isSel ? "bg-emerald-500/10" : "hover:bg-white/[0.02]"}`}
                           onClick={() => toggle(e.id)}
                           data-testid={`record-service-pick-${e.id}`}>
                        {isSel
                          ? <CheckSquare className="w-4 h-4 text-emerald-400 shrink-0" />
                          : <Square className="w-4 h-4 text-[#647087] shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-white truncate flex items-center gap-2">
                            <span className="truncate">{e.name}</span>
                            {isNonServiceable && (
                              <span
                                className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 shrink-0"
                                title="Marked replaced or repaired"
                              >
                                Replaced/Repaired
                              </span>
                            )}
                          </div>
                          <div className="text-[9px] text-[#647087]">
                            {e.quantity || 0} {e.unit || "pcs"}
                            {e.interval_days ? ` · every ${e.interval_days}d` : ""}
                            {e.next_service_date ? ` · next ${new Date(e.next_service_date).toLocaleDateString("en-GB")}` : ""}
                          </div>
                        </div>
                        {isSel && (
                          <input type="number"
                                 min="0"
                                 step="0.1"
                                 value={qtyOverride[e.id] ?? (e.quantity || 1)}
                                 onClick={(ev) => ev.stopPropagation()}
                                 onChange={(ev) => setQtyOverride((q) => ({ ...q, [e.id]: ev.target.value }))}
                                 className="hv-input text-xs w-16 shrink-0"
                                 data-testid={`record-service-qty-${e.id}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {step === "confirm" && (
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
                Confirmation · {confirmRows.length} item{confirmRows.length === 1 ? "" : "s"}
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[9px] uppercase tracking-wider text-[#647087] border-b border-white/10">
                    <th className="text-left py-1.5 pr-2">Item</th>
                    <th className="text-left py-1.5 pr-2">Last serviced</th>
                    <th className="text-left py-1.5 pr-2">Next service</th>
                  </tr>
                </thead>
                <tbody>
                  {confirmRows.map((r) => (
                    <RowFrag key={r.equipment_id} r={r} prompts={intervalPrompts} patchRow={patchRow} />
                  ))}
                </tbody>
              </table>
              {warnings.length > 0 && (
                <div className="border border-amber-500/40 bg-amber-500/10 rounded p-3 text-xs"
                     data-testid="record-service-warnings">
                  <div className="flex items-center gap-2 text-amber-300 font-bold text-[11px] mb-2">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Not on the linked job card's picking / materials
                  </div>
                  <ul className="list-disc pl-5 space-y-1 text-amber-200">
                    {warnings.filter((w) => w.reason === "not_on_picking").map((w, i) => (
                      <li key={i}>
                        <b>{w.equipment_name}</b> — {w.detail || "not on picking"}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2 text-[10px] text-amber-200/80">
                    You can proceed anyway (the service will still be logged), or go back and untick these items.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-white/10 px-4 py-3 flex items-center justify-between">
          {step === "pick" ? (
            <>
              <div className="text-[10px] text-[#647087]">{selected.size} selected</div>
              <div className="flex gap-2">
                <button type="button" onClick={onClose}
                        className="hv-btn hv-btn-ghost text-xs"
                        data-testid="record-service-cancel">Cancel</button>
                <button type="button" onClick={goConfirm}
                        className="hv-btn text-xs"
                        disabled={selected.size === 0}
                        data-testid="record-service-next">Next</button>
              </div>
            </>
          ) : (
            <>
              <button type="button" onClick={() => setStep("pick")}
                      className="hv-btn hv-btn-ghost text-xs"
                      data-testid="record-service-back">Back</button>
              <div className="flex gap-2">
                {warnings.length > 0 ? (
                  <button type="button" onClick={acknowledgeAndSave}
                          className="hv-btn text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40"
                          disabled={saving}
                          data-testid="record-service-acknowledge">
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    Save anyway
                  </button>
                ) : (
                  <button type="button" onClick={save}
                          className="hv-btn text-xs"
                          disabled={saving}
                          data-testid="record-service-save">
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    Save
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function RowFrag({ r, prompts, patchRow }) {
  // Iter 425.f — Extracted to render the row + optional interval
  // prompt as sibling <tr>s without React Fragment key warnings.
  return (
    <>
      <tr className="border-b border-white/[0.03]"
          data-testid={`record-service-confirm-${r.equipment_id}`}>
        <td className="py-2 pr-2 text-white">
          {r.name}
          {r.interval_days
            ? <span className="text-[9px] text-[#647087] block">interval {r.interval_days}d</span>
            : null}
        </td>
        <td className="py-2 pr-2 text-[#a0aabf]">
          {new Date(r.last_service_date).toLocaleDateString("en-GB")}
        </td>
        <td className="py-2 pr-2">
          <input type="date"
                 value={r.next_service_date || ""}
                 onChange={(e) => patchRow(r.equipment_id, { next_service_date: e.target.value })}
                 className="hv-input text-xs"
                 data-testid={`record-service-next-${r.equipment_id}`} />
        </td>
      </tr>
      {prompts[r.equipment_id] && (
        <tr>
          <td colSpan={3} className="pb-2">
            <div className="ml-2 border-l-2 border-amber-500/50 pl-2 text-[10px] text-amber-300 space-y-1"
                 data-testid={`record-service-interval-prompt-${r.equipment_id}`}>
              <div>
                You changed the next service date. Apply as a permanent interval or just this cycle?
              </div>
              <div className="flex gap-2">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio"
                         checked={r.update_interval_permanent === true}
                         onChange={() => patchRow(r.equipment_id, { update_interval_permanent: true })}
                         data-testid={`record-service-permanent-${r.equipment_id}`} />
                  <span>Update interval permanently</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio"
                         checked={r.update_interval_permanent === false}
                         onChange={() => patchRow(r.equipment_id, { update_interval_permanent: false })}
                         data-testid={`record-service-onetime-${r.equipment_id}`} />
                  <span>Only this service</span>
                </label>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
